const WARNING_MS = 300 * 1000
const ONE_MINUTE_MS = 60 * 1000
const COUNTDOWN_CHECKPOINTS_MS = [
  4 * ONE_MINUTE_MS,
  3 * ONE_MINUTE_MS,
  2 * ONE_MINUTE_MS,
  ONE_MINUTE_MS,
  40 * 1000,
  20 * 1000
]

export class SessionTimeoutWarning {
  constructor(dialog) {
    if (!dialog) return

    const ttlSeconds = Number(dialog.dataset.sessionTtl)
    if (!ttlSeconds) return

    this._ttlMs = ttlSeconds * 1000
    this._dialog = dialog
    this._countdown = dialog.querySelector('[data-countdown]')
    this._countdownLive = dialog.querySelector('[data-countdown-live]')
    this._description = dialog.querySelector('#session-timeout-warning-description')
    this._minuteSingular = dialog.dataset.minuteSingular || 'minute'
    this._minutePlural = dialog.dataset.minutePlural || 'minutes'
    this._secondPlural = dialog.dataset.secondPlural || 'seconds'

    if (!this._countdown) return

    this._warningTimer = null
    this._expiredTimer = null
    this._countdownTimers = []

    dialog.querySelector('[data-keep-session]')?.addEventListener('click', () => this._keepAlive())
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault()
      this._keepAlive()
    })

    this._startTimers()
  }

  _startTimers() {
    clearTimeout(this._warningTimer)
    clearTimeout(this._expiredTimer)
    this._clearCountdownTimers()

    const warningDelay = Math.max(0, this._ttlMs - WARNING_MS)
    this._warningTimer = setTimeout(() => this._showWarning(), warningDelay)
    this._expiredTimer = setTimeout(() => this._expireSession(), this._ttlMs)
  }

  _expireSession() {
    window.location.assign('/session-ended')
  }

  _showWarning() {
    this._remainingMs = Math.min(WARNING_MS, this._ttlMs)
    this._updateCountdown()
    this._dialog.showModal()

    this._scheduleCountdownUpdates()
  }

  _scheduleCountdownUpdates() {
    this._clearCountdownTimers()

    for (const checkpointMs of COUNTDOWN_CHECKPOINTS_MS) {
      const delayMs = this._remainingMs - checkpointMs

      if (delayMs <= 0) continue

      const timer = setTimeout(() => {
        this._remainingMs = checkpointMs
        this._updateCountdown()
      }, delayMs)

      this._countdownTimers.push(timer)
    }
  }

  _clearCountdownTimers() {
    for (const timer of this._countdownTimers) {
      clearTimeout(timer)
    }

    this._countdownTimers = []
  }

  _updateCountdown() {
    const timeText =
      this._remainingMs < ONE_MINUTE_MS
        ? this._formatSeconds(this._remainingMs)
        : this._formatMinutes(this._remainingMs)

    this._countdown.textContent = timeText

    if (this._countdownLive) {
      this._countdownLive.textContent = this._description?.textContent?.trim() || timeText
    }
  }

  _formatMinutes(remainingMs) {
    const minutes = Math.ceil(remainingMs / ONE_MINUTE_MS)
    return `${minutes} ${minutes === 1 ? this._minuteSingular : this._minutePlural}`
  }

  _formatSeconds(remainingMs) {
    const seconds = Math.ceil(remainingMs / 1000)
    return `${seconds} ${this._secondPlural}`
  }

  async _keepAlive() {
    clearTimeout(this._warningTimer)
    clearTimeout(this._expiredTimer)
    this._clearCountdownTimers()

    this._dialog.close()
    const main = document.querySelector('main')
    if (main) main.focus()

    this._startTimers()
    try {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
      await fetch(`/session/keep-alive?returnTo=${returnTo}`, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store'
      })
    } catch {
      // If keep-alive fails, next navigation will handle session expiry naturally.
    }
  }
}
